/**
 * MediaService - Real-time P2P media communication
 * Extracted from realP2PDemo.html with complete functionality
 * Handles voice calls, video calls, screen sharing, and media streaming
 */

import { DataConnection } from 'peerjs'
import { toastManager, TOAST_GROUPS } from '../utils/toastManager'
import { EventEmitter } from './EventEmitter'
import { notificationSoundService } from './NotificationSoundService'
import { routeDetectionService } from './RouteDetectionService'

export interface CallRequest {
  type: 'voice-call-request' | 'video-call-request'
  from: string
  fromAlias: string
  timestamp: number
}

export interface CallResponse {
  type: 'voice-call-response' | 'video-call-response'
  from: string
  fromAlias: string
  accepted: boolean
  timestamp: number
}

export interface ScreenShareStart {
  type: 'screen-share-start'
  from: string
  fromAlias: string
  timestamp: number
}

export interface ScreenShareEnd {
  type: 'screen-share-end'
  from: string
  timestamp: number
}

export interface CallState {
  isInCall: boolean
  callType: 'voice' | 'video' | 'screen' | null
  localStream: MediaStream | null
  remoteStream: MediaStream | null
  activePeer: string | null
  activePeerAlias: string | null
  activePeerAvatar: string | null
  callStartTime: Date | null
  // Progressive call setup states
  callProgress: 'idle' | 'initializing' | 'connecting' | 'ringing' | 'connected' | 'failed'
  callError: string | null
}

export class MediaService extends EventEmitter {
  private _localPeerId: string
  private _localAlias: string
  private _peer: any // PeerJS instance
  private _currentMediaCall: any // PeerJS MediaCall instance
  private _aliasRegistry: any // AliasRegistryService for enhanced alias resolution

  // Media stream caching and permission management
  private cachedAudioStream: MediaStream | null = null
  private cachedVideoStream: MediaStream | null = null
  private permissionStates = {
    camera: 'unknown' as PermissionState | 'unknown',
    microphone: 'unknown' as PermissionState | 'unknown'
  }
  private pendingPermissionRequests = new Set<string>()

  // CRITICAL FIX: Audio element for voice call remote stream playback
  private remoteAudioElement: HTMLAudioElement | null = null

  // CONTEXTUAL CALL ENHANCEMENT: Navigation-aware call state management
  private routeChangeUnsubscribe: (() => void) | null = null
  private callStateBeforeNavigation: CallState | null = null
  private isNavigatingDuringCall = false

  constructor(localPeerId: string, localAlias: string, peer?: any, aliasRegistry?: any) {
    super()
    this._localPeerId = localPeerId
    this._localAlias = localAlias
    this._peer = peer
    this._aliasRegistry = aliasRegistry

    // CRITICAL FIX: Initialize audio element for remote stream playback
    this.initializeRemoteAudioElement()

    // Initialize permission checking
    this.initializePermissionMonitoring()

    // CONTEXTUAL CALL ENHANCEMENT: Initialize route change monitoring for call state persistence
    this.initializeNavigationTracking()
  }
  private callState: CallState = {
    isInCall: false,
    callType: null,
    localStream: null,
    remoteStream: null,
    activePeer: null,
    activePeerAlias: null,
    activePeerAvatar: null,
    callStartTime: null,
    callProgress: 'idle',
    callError: null
  }

  private callListeners: ((state: CallState) => void)[] = []
  private screenStream: MediaStream | null = null

  /**
   * CRITICAL FIX: Initialize remote audio element for voice call playback
   * MOBILE FIX: Enhanced mobile-specific audio setup
   */
  private initializeRemoteAudioElement(): void {
    try {
      this.remoteAudioElement = document.createElement('audio')

      // Mobile-specific audio configuration
      this.remoteAudioElement.autoplay = true
      this.remoteAudioElement.volume = 1.0
      this.remoteAudioElement.controls = false

      // MOBILE FIX: Critical attributes for mobile audio playback
      this.remoteAudioElement.setAttribute('playsinline', 'true')
      this.remoteAudioElement.setAttribute('webkit-playsinline', 'true')
      this.remoteAudioElement.setAttribute('x5-playsinline', 'true') // WeChat browser

      // Mobile audio unlock handling
      this.remoteAudioElement.load() // Preload audio element

      console.log('✅ Remote audio element initialized for voice calls with mobile enhancements')
    } catch (error) {
      console.error('❌ Failed to initialize remote audio element:', error)
    }
  }

  /**
   * CRITICAL FIX: Handle remote stream audio/video playback
   * MOBILE FIX: Enhanced mobile audio handling with interaction unlocking
   */
  private handleRemoteStreamPlayback(remoteStream: MediaStream, callType: 'voice' | 'video'): void {
    try {
      if (callType === 'voice' && this.remoteAudioElement) {
        // For voice calls, play audio through the dedicated audio element
        this.remoteAudioElement.srcObject = remoteStream

        // CRITICAL FIX: Append audio element to DOM for proper playback
        if (!document.body.contains(this.remoteAudioElement)) {
          this.remoteAudioElement.style.display = 'none' // Hidden but in DOM
          document.body.appendChild(this.remoteAudioElement)
          console.log('🔊 Remote audio element added to DOM')
        }

        // MOBILE FIX: Enhanced mobile audio playback with multiple strategies
        this.playRemoteAudioWithMobileFallback()
        console.log('✅ Remote voice stream connected to audio element with mobile support')
      } else if (callType === 'video') {
        // For video calls, also handle audio from the video stream
        const audioTracks = remoteStream.getAudioTracks()
        if (audioTracks.length > 0 && this.remoteAudioElement) {
          // Create audio-only stream for video call audio
          const audioStream = new MediaStream(audioTracks)
          this.remoteAudioElement.srcObject = audioStream

          // CRITICAL FIX: Ensure audio element is in DOM for video calls too
          if (!document.body.contains(this.remoteAudioElement)) {
            this.remoteAudioElement.style.display = 'none'
            document.body.appendChild(this.remoteAudioElement)
            console.log('🔊 Remote audio element added to DOM for video call')
          }

          // MOBILE FIX: Enhanced mobile audio playback for video calls
          this.playRemoteAudioWithMobileFallback()
          console.log('✅ Video call audio stream connected to dedicated audio element with mobile support')
        }
        console.log('✅ Remote video stream will be handled by VideoCallUI component')
      }
    } catch (error) {
      console.error('❌ Failed to setup remote stream playback:', error)
    }
  }

  /**
   * MOBILE FIX: Enhanced mobile audio playback with comprehensive fallback strategies
   */
  private playRemoteAudioWithMobileFallback(): void {
    if (!this.remoteAudioElement) {
      console.error('❌ No remote audio element available for mobile playback')
      return
    }

    const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    console.log(`📱 Attempting audio playback - Mobile: ${isMobile}`)

    // Immediate play attempt
    this.remoteAudioElement.play().then(() => {
      console.log('✅ Remote audio playing immediately')
    }).catch(error => {
      console.warn('⚠️ Initial remote audio play failed:', error)

      // Mobile-specific fallback strategies
      if (isMobile) {
        console.log('📱 Implementing mobile audio unlock strategies')

        // Strategy 1: Multiple interaction event types
        const playOnInteraction = async () => {
          try {
            await this.remoteAudioElement?.play()
            console.log('✅ Remote audio playing after mobile interaction')
            this.cleanupMobileAudioListeners()
          } catch (retryError) {
            console.error('❌ Mobile audio play after interaction failed:', retryError)

            // Strategy 2: Delayed retry with audio context unlock
            setTimeout(async () => {
              try {
                if (this.remoteAudioElement) {
                  this.remoteAudioElement.load() // Reload the audio element
                  await this.remoteAudioElement.play()
                  console.log('✅ Remote audio playing after reload and delay')
                }
              } catch (finalError) {
                console.error('❌ Final mobile audio play attempt failed:', finalError)
              }
            }, 1000)
          }
        }

        // Store interaction handlers for cleanup
        this.mobileAudioPlayHandler = playOnInteraction

        // Multiple interaction event types for maximum compatibility
        document.addEventListener('touchstart', playOnInteraction, { once: true, passive: true })
        document.addEventListener('touchend', playOnInteraction, { once: true, passive: true })
        document.addEventListener('click', playOnInteraction, { once: true })
        document.addEventListener('pointerdown', playOnInteraction, { once: true })

        // Auto-cleanup after 30 seconds
        setTimeout(() => {
          this.cleanupMobileAudioListeners()
        }, 30000)

        console.log('📱 Mobile audio interaction listeners registered')
      } else {
        // Desktop fallback - multiple retry attempts
        console.log('🖥️ Implementing desktop audio retry strategies')

        const retryIntervals = [100, 500, 1000, 2000]
        retryIntervals.forEach((delay, index) => {
          setTimeout(async () => {
            try {
              await this.remoteAudioElement?.play()
              console.log(`✅ Remote audio playing after ${delay}ms desktop retry`)
            } catch (retryError) {
              console.warn(`❌ Desktop audio retry ${index + 1} failed:`, retryError)
            }
          }, delay)
        })
      }
    })
  }

  private mobileAudioPlayHandler: (() => Promise<void>) | null = null

  /**
   * MOBILE FIX: Clean up mobile audio interaction listeners
   */
  private cleanupMobileAudioListeners(): void {
    if (this.mobileAudioPlayHandler) {
      document.removeEventListener('touchstart', this.mobileAudioPlayHandler)
      document.removeEventListener('touchend', this.mobileAudioPlayHandler)
      document.removeEventListener('click', this.mobileAudioPlayHandler)
      document.removeEventListener('pointerdown', this.mobileAudioPlayHandler)
      this.mobileAudioPlayHandler = null
      console.log('📱 Mobile audio interaction listeners cleaned up')
    }
  }

  /**
   * CONTEXTUAL CALL ENHANCEMENT: Initialize navigation tracking for call state persistence
   */
  private initializeNavigationTracking(): void {
    // Monitor route changes to ensure call state persistence during navigation
    this.routeChangeUnsubscribe = routeDetectionService.onRouteChange((data) => {
      const { oldPath, newPath, routeInfo } = data

      // Only handle navigation during active calls
      if (this.callState.isInCall) {
        console.log('🧭 CALL STATE PERSISTENCE: Navigation detected during active call')
        console.log('🧭 From:', oldPath, 'To:', newPath)
        console.log('🧭 Call state:', this.callState.callType, this.callState.callProgress)

        // Store call state before navigation
        this.callStateBeforeNavigation = { ...this.callState }
        this.isNavigatingDuringCall = true

        // Ensure media streams remain active during navigation
        this.preserveMediaStreamsDuringNavigation()

        // Emit event for UI components to handle navigation-aware rendering
        this.emit('navigationDuringCall', {
          oldPath,
          newPath,
          routeInfo,
          callState: this.callState
        })

        // Reset navigation flag after a short delay
        setTimeout(() => {
          this.isNavigatingDuringCall = false
          console.log('🧭 CALL STATE PERSISTENCE: Navigation handling completed')
        }, 500)
      }
    })

    console.log('🧭 Navigation tracking initialized for call state persistence')
  }

  /**
   * CONTEXTUAL CALL ENHANCEMENT: Preserve media streams during navigation
   */
  private preserveMediaStreamsDuringNavigation(): void {
    if (!this.callState.isInCall) return

    console.log('🧭 CALL STATE PERSISTENCE: Preserving media streams during navigation')

    // Ensure local stream remains active
    if (this.callState.localStream) {
      const tracks = this.callState.localStream.getTracks()
      tracks.forEach(track => {
        if (track.readyState === 'ended') {
          console.warn('⚠️ Track ended during navigation, may need to restart:', track.kind)
        } else {
          console.log('✅ Track preserved during navigation:', track.kind, track.readyState)
        }
      })
    }

    // Ensure remote stream playback continues
    if (this.callState.remoteStream && this.remoteAudioElement) {
      if (this.remoteAudioElement.paused) {
        console.log('🧭 Resuming remote audio playback after navigation')
        this.remoteAudioElement.play().catch(error => {
          console.warn('⚠️ Failed to resume remote audio after navigation:', error)
        })
      }
    }

    // Ensure PeerJS media call remains active
    if (this._currentMediaCall && this._currentMediaCall.open) {
      console.log('✅ PeerJS media call remains active during navigation')
    } else {
      console.warn('⚠️ PeerJS media call may have been disrupted during navigation')
    }
  }

  /**
   * Initialize permission monitoring for proactive permission management
   */
  private async initializePermissionMonitoring(): Promise<void> {
    try {
      // Check if Permissions API is supported
      if ('permissions' in navigator) {
        // Monitor microphone permission
        try {
          const micPermission = await navigator.permissions.query({ name: 'microphone' as PermissionName })
          this.permissionStates.microphone = micPermission.state
          micPermission.addEventListener('change', () => {
            this.permissionStates.microphone = micPermission.state
            console.log(`🎤 Microphone permission changed to: ${micPermission.state}`)
            if (micPermission.state === 'denied') {
              this.handlePermissionDenied('microphone')
            }
          })
        } catch (e) {
          console.log('📱 Microphone permission query not supported')
        }

        // Monitor camera permission
        try {
          const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName })
          this.permissionStates.camera = cameraPermission.state
          cameraPermission.addEventListener('change', () => {
            this.permissionStates.camera = cameraPermission.state
            console.log(`📹 Camera permission changed to: ${cameraPermission.state}`)
            if (cameraPermission.state === 'denied') {
              this.handlePermissionDenied('camera')
            }
          })
        } catch (e) {
          console.log('📱 Camera permission query not supported')
        }
      }
    } catch (error) {
      console.log('📱 Permissions API not fully supported, using fallback detection')
    }
  }

  /**
   * Handle permission denied scenarios with graceful fallbacks
   */
  private handlePermissionDenied(deviceType: 'microphone' | 'camera'): void {
    // Clean up cached streams for denied device
    if (deviceType === 'microphone' || deviceType === 'camera') {
      if (this.cachedAudioStream && deviceType === 'microphone') {
        this.cachedAudioStream.getTracks().forEach(track => track.stop())
        this.cachedAudioStream = null
      }
      if (this.cachedVideoStream && deviceType === 'camera') {
        this.cachedVideoStream.getTracks().forEach(track => track.stop())
        this.cachedVideoStream = null
      }
    }
  }

  /**
   * Smart media stream acquisition with permission checking and caching
   * This is the core fix for "Microphone access denied" issues
   * DESKTOP ENHANCEMENT: Force permissions aggressively for desktop users
   */
  private async getSmartMediaStream(constraints: MediaStreamConstraints): Promise<MediaStream> {
    const needsAudio = constraints.audio !== false
    const needsVideo = constraints.video !== false

    console.log(`🎯 Smart media request: audio=${needsAudio}, video=${needsVideo}`)

    // CRITICAL iOS FIX: Detect iOS Safari specifically
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !!(window as any).MSStream === false
    const isDesktop = !(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))

    if (isIOSSafari) {
      console.log('📱 iOS Safari detected - using iOS-optimized getUserMedia strategy')
    } else if (isDesktop) {
      console.log('🖥️ Desktop user detected - using enhanced permission strategy')
    }

    // CRITICAL iOS FIX: For iOS Safari, use cloning strategy to avoid multiple getUserMedia calls
    if (isIOSSafari && needsVideo && this.cachedVideoStream) {
      const videoTracks = this.cachedVideoStream.getVideoTracks()
      const audioTracks = this.cachedVideoStream.getAudioTracks()

      if (videoTracks.length > 0 && videoTracks[0]?.readyState === 'live') {
        console.log('📱 iOS Safari: Cloning existing video stream to avoid multiple getUserMedia calls')

        // Create new MediaStream with cloned tracks
        const clonedStream = new MediaStream()

        // Clone video tracks
        videoTracks.forEach(track => {
          clonedStream.addTrack(track.clone())
        })

        // Clone audio tracks if needed
        if (needsAudio && audioTracks.length > 0) {
          audioTracks.forEach(track => {
            clonedStream.addTrack(track.clone())
          })
        }

        console.log('📱 iOS Safari: Cloned stream created with tracks:', clonedStream.getTracks().length)
        return clonedStream
      }
    }

    // Check for existing streams first (as user suggested: "if already use the same")
    if (needsAudio && !needsVideo && this.cachedAudioStream) {
      const audioTracks = this.cachedAudioStream.getAudioTracks()
      if (audioTracks.length > 0 && audioTracks[0]?.readyState === 'live') {
        console.log('✅ Reusing existing audio stream')
        return this.cachedAudioStream
      } else {
        // Clean up dead stream
        this.cachedAudioStream = null
      }
    }

    if (needsVideo && this.cachedVideoStream && !isIOSSafari) {
      const videoTracks = this.cachedVideoStream.getVideoTracks()
      const audioTracks = this.cachedVideoStream.getAudioTracks()
      const hasLiveVideo = videoTracks.length > 0 && videoTracks[0]?.readyState === 'live'
      const hasLiveAudio = !needsAudio || (audioTracks.length > 0 && audioTracks[0]?.readyState === 'live')

      if (hasLiveVideo && hasLiveAudio) {
        console.log('✅ Reusing existing video stream')
        return this.cachedVideoStream
      } else {
        // Clean up dead stream
        this.cachedVideoStream = null
      }
    }

    // Create a unique key for this request type
    const requestKey = `${needsAudio ? 'audio' : ''}-${needsVideo ? 'video' : ''}`

    // Prevent multiple simultaneous requests for the same media type
    if (this.pendingPermissionRequests.has(requestKey)) {
      console.log('⏳ Permission request already pending, waiting...')
      // Wait for ongoing request to complete
      await new Promise(resolve => {
        const checkInterval = setInterval(() => {
          if (!this.pendingPermissionRequests.has(requestKey)) {
            clearInterval(checkInterval)
            resolve(null)
          }
        }, 100)
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkInterval)
          resolve(null)
        }, 10000)
      })

      // Try to return cached stream after waiting
      if (needsAudio && !needsVideo && this.cachedAudioStream) {
        return this.cachedAudioStream
      }
      if (needsVideo && this.cachedVideoStream) {
        return this.cachedVideoStream
      }
    }

    this.pendingPermissionRequests.add(requestKey)

    try {
      // SURGICAL FIX: Remove aggressive permission caching - allow retry attempts
      // Only show warning for denied permissions, don't block the request
      if (needsAudio && this.permissionStates.microphone === 'denied') {
        console.warn('⚠️ Microphone was previously denied, attempting retry...')
      }

      if (needsVideo && this.permissionStates.camera === 'denied') {
        console.warn('⚠️ Camera was previously denied, attempting retry...')
      }

      console.log('🎤 Requesting new media stream...')

      // CRITICAL iOS FIX: Use iOS-optimized constraints for Safari
      let enhancedConstraints = constraints
      if (isIOSSafari) {
        enhancedConstraints = this.getIOSOptimizedConstraints(constraints)
        console.log('📱 Using iOS Safari-optimized constraints:', enhancedConstraints)
      } else if (isDesktop) {
        enhancedConstraints = this.getDesktopOptimizedConstraints(constraints)
        console.log('🖥️ Using desktop-optimized constraints:', enhancedConstraints)
      }

      // Request media with enhanced error handling
      const stream = await navigator.mediaDevices.getUserMedia(enhancedConstraints)

      console.log(`✅ Media stream acquired successfully: ${stream.getTracks().length} tracks`)

      // DESKTOP ENHANCEMENT: Log detailed track information for desktop users
      if (isDesktop) {
        stream.getTracks().forEach(track => {
          const settings = track.getSettings()
          console.log(`🖥️ Desktop track ${track.kind}:`, {
            label: track.label,
            deviceId: settings.deviceId,
            ...(track.kind === 'video' && {
              width: settings.width,
              height: settings.height,
              frameRate: settings.frameRate
            }),
            ...(track.kind === 'audio' && {
              sampleRate: settings.sampleRate,
              channelCount: settings.channelCount
            })
          })
        })
      }

      // Cache the stream for reuse
      if (needsVideo) {
        this.cachedVideoStream = stream
      } else if (needsAudio) {
        this.cachedAudioStream = stream
      }

      // Update permission states based on successful access
      if (needsAudio) {
        this.permissionStates.microphone = 'granted'
      }
      if (needsVideo) {
        this.permissionStates.camera = 'granted'
      }

      return stream

    } catch (error) {
      console.error('❌ Media stream request failed:', error)

      // Update permission states
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          if (needsAudio) this.permissionStates.microphone = 'denied'
          if (needsVideo) this.permissionStates.camera = 'denied'

          // Provide specific guidance based on device type
          const deviceTypes = []
          if (needsAudio) deviceTypes.push('microphone')
          if (needsVideo) deviceTypes.push('camera')

          // SURGICAL FIX: Provide retry-friendly guidance instead of demanding page refresh
          const isChrome = navigator.userAgent.includes('Chrome')
          const isFirefox = navigator.userAgent.includes('Firefox')
          const isEdge = navigator.userAgent.includes('Edg')

          let browserGuidance = ''
          if (isChrome) {
            browserGuidance = 'In Chrome: Click the camera/microphone icon in the address bar → Select "Allow" → Then try again'
          } else if (isFirefox) {
            browserGuidance = 'In Firefox: Click the shield icon → Select "Allow" for camera/microphone → Then try again'
          } else if (isEdge) {
            browserGuidance = 'In Edge: Click the camera/microphone icon in the address bar → Select "Allow" → Then try again'
          } else {
            browserGuidance = 'Click the camera/microphone icon in your browser\'s address bar, select "Allow", then try again'
          }

          throw new Error(
            `${deviceTypes.join('/')} access denied. ${browserGuidance}`
          )
        } else if (error.name === 'NotFoundError' || error.name === 'DeviceNotFoundError') {
          const deviceTypes = []
          if (needsAudio) deviceTypes.push('microphone')
          if (needsVideo) deviceTypes.push('camera')

          throw new Error(`No ${deviceTypes.join('/')} device found. Please check your device connections.`)
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          throw new Error('Device is already in use by another application. Please close other applications using the device and try again.')
        } else if (error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError') {
          // DESKTOP ENHANCEMENT: Aggressive fallback strategy for desktop
          console.log('⚠️ Constraints too strict, trying desktop fallback strategy...')

          if (isDesktop) {
            // Try multiple fallback strategies for desktop
            const fallbackStrategies = [
              // Strategy 1: High quality desktop fallback
              {
                audio: needsAudio ? {
                  echoCancellation: true,
                  noiseSuppression: true,
                  autoGainControl: true,
                  sampleRate: 48000
                } : false,
                video: needsVideo ? {
                  width: { ideal: 1280 },
                  height: { ideal: 720 },
                  frameRate: { ideal: 30 }
                } : false
              },
              // Strategy 2: Standard quality
              {
                audio: needsAudio ? {
                  echoCancellation: true,
                  noiseSuppression: true
                } : false,
                video: needsVideo ? {
                  width: { ideal: 640 },
                  height: { ideal: 480 }
                } : false
              },
              // Strategy 3: Basic fallback
              {
                audio: needsAudio ? true : false,
                video: needsVideo ? true : false
              }
            ]

            for (const strategy of fallbackStrategies) {
              try {
                console.log('🖥️ Trying desktop fallback strategy:', strategy)
                return await navigator.mediaDevices.getUserMedia(strategy)
              } catch (fallbackError) {
                console.warn('Desktop fallback strategy failed:', fallbackError)
              }
            }
          }

          // Final basic fallback
          const basicFallback: MediaStreamConstraints = {
            audio: needsAudio ? true : false,
            video: needsVideo ? true : false
          }
          return await navigator.mediaDevices.getUserMedia(basicFallback)
        }
      }

      throw error

    } finally {
      this.pendingPermissionRequests.delete(requestKey)
    }
  }

  /**
   * DESKTOP ENHANCEMENT: Get desktop-optimized media constraints
   */
  private getDesktopOptimizedConstraints(baseConstraints: MediaStreamConstraints): MediaStreamConstraints {
    const enhanced: MediaStreamConstraints = { ...baseConstraints }

    // Enhanced audio constraints for desktop
    if (enhanced.audio && typeof enhanced.audio === 'object') {
      enhanced.audio = {
        ...enhanced.audio,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: { ideal: 48000 },
        channelCount: { ideal: 2 },
        // Note: latency and volume are experimental/non-standard constraints
        // latency: { ideal: 0.01 }, // Low latency for desktop
        // volume: { ideal: 1.0 }
      }
    } else if (enhanced.audio === true) {
      enhanced.audio = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        sampleRate: { ideal: 48000 },
        channelCount: { ideal: 2 },
        // Note: latency and volume are experimental/non-standard constraints
        // latency: { ideal: 0.01 },
        // volume: { ideal: 1.0 }
      }
    }

    // Enhanced video constraints for desktop
    if (enhanced.video && typeof enhanced.video === 'object') {
      enhanced.video = {
        ...enhanced.video,
        width: { ideal: 1920, min: 640 },
        height: { ideal: 1080, min: 480 },
        frameRate: { ideal: 60, min: 15 },
        facingMode: 'user',
        // Note: resizeMode is experimental/non-standard
        // resizeMode: 'crop-and-scale'
      }
    } else if (enhanced.video === true) {
      enhanced.video = {
        width: { ideal: 1920, min: 640 },
        height: { ideal: 1080, min: 480 },
        frameRate: { ideal: 60, min: 15 },
        facingMode: 'user',
        // Note: resizeMode is experimental/non-standard
        // resizeMode: 'crop-and-scale'
      }
    }

    return enhanced
  }

  /**
   * CRITICAL iOS FIX: Get iOS Safari-optimized media constraints
   * Addresses iOS Safari black screen issues and autoplay restrictions
   */
  private getIOSOptimizedConstraints(baseConstraints: MediaStreamConstraints): MediaStreamConstraints {
    const enhanced: MediaStreamConstraints = { ...baseConstraints }

    // iOS Safari audio constraints - simpler and more compatible
    if (enhanced.audio && typeof enhanced.audio === 'object') {
      enhanced.audio = {
        ...enhanced.audio,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false, // iOS Safari sometimes struggles with AGC
        sampleRate: { ideal: 44100 }, // iOS Safari preferred sample rate
        channelCount: { ideal: 1 }, // Mono for better iOS compatibility
      }
    } else if (enhanced.audio === true) {
      enhanced.audio = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: false,
        sampleRate: { ideal: 44100 },
        channelCount: { ideal: 1 },
      }
    }

    // iOS Safari video constraints - optimized for iOS webkit rendering
    if (enhanced.video && typeof enhanced.video === 'object') {
      enhanced.video = {
        ...enhanced.video,
        width: { ideal: 640, max: 1280 }, // iOS Safari prefers lower resolutions
        height: { ideal: 480, max: 720 }, // Avoid high resolutions that cause black screen
        frameRate: { ideal: 30, max: 30 }, // Fixed 30fps for iOS Safari stability
        facingMode: 'user',
        // iOS Safari specific optimizations
        aspectRatio: { ideal: 4/3 }, // More compatible aspect ratio for iOS
      }
    } else if (enhanced.video === true) {
      enhanced.video = {
        width: { ideal: 640, max: 1280 },
        height: { ideal: 480, max: 720 },
        frameRate: { ideal: 30, max: 30 },
        facingMode: 'user',
        aspectRatio: { ideal: 4/3 },
      }
    }

    console.log('📱 iOS Safari constraints optimized:', enhanced)
    return enhanced
  }

  /**
   * DESKTOP ENHANCEMENT: Force permissions proactively for desktop users
   * FIXED: Better error handling, user guidance, and iOS compatibility
   */
  async forceDesktopPermissions(): Promise<{
    audioPermission: boolean
    videoPermission: boolean
    error?: string
    userGuidance?: string
    permissionFlow?: 'immediate' | 'guided' | 'manual'
  }> {
    const isDesktop = !(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !!(window as any).MSStream === false

    if (!isDesktop && !isIOSSafari) {
      return { audioPermission: false, videoPermission: false, error: 'Not a desktop or iOS device', permissionFlow: 'manual' }
    }

    console.log(`🖥️ Forcing permissions for ${isIOSSafari ? 'iOS Safari' : 'desktop'}...`)

    // EMERGENCY DEBUG: Check current permission states
    if ('permissions' in navigator) {
      try {
        const micCheck = await navigator.permissions.query({ name: 'microphone' as PermissionName })
        const cameraCheck = await navigator.permissions.query({ name: 'camera' as PermissionName })
        console.log('🔍 INITIAL PERMISSION STATE:', {
          microphone: micCheck.state,
          camera: cameraCheck.state
        })
      } catch (permError) {
        console.warn('⚠️ Permission check failed:', permError)
      }
    }

    const results: {
      audioPermission: boolean
      videoPermission: boolean
      error?: string
      userGuidance?: string
      permissionFlow?: 'immediate' | 'guided' | 'manual'
    } = {
      audioPermission: false,
      videoPermission: false,
      permissionFlow: 'immediate'
    }

    try {
      // Force audio permission first with iOS Safari optimization
      try {
        // iOS SAFARI FIX: Use iOS-optimized audio constraints
        const audioConstraints = isIOSSafari ? {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 44100 }, // iOS Safari prefers 44.1kHz
          channelCount: { ideal: 1 } // Mono for iOS compatibility
        } : {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 48000 },
          channelCount: { ideal: 2 }
        }

        console.log(`🎤 Requesting audio with ${isIOSSafari ? 'iOS Safari' : 'desktop'} optimized constraints:`, audioConstraints)

        const audioStream = await this.getSmartMediaStream({
          audio: audioConstraints,
          video: false
        })

        results.audioPermission = true
        console.log(`✅ ${isIOSSafari ? 'iOS Safari' : 'Desktop'} audio permission granted`)

        // Keep stream cached for immediate use
        this.cachedAudioStream = audioStream

        // iOS SAFARI FIX: Test audio playback capability
        if (isIOSSafari) {
          try {
            // Test if we can create audio context (required for iOS audio)
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
            console.log('📱 iOS Safari: Audio context created successfully')

            // Resume audio context if suspended (iOS requirement)
            if (audioContext.state === 'suspended') {
              await audioContext.resume()
              console.log('📱 iOS Safari: Audio context resumed')
            }

            audioContext.close()
          } catch (contextError) {
            console.warn('📱 iOS Safari: Audio context test failed:', contextError)
          }
        }

      } catch (audioError) {
        console.warn(`❌ ${isIOSSafari ? 'iOS Safari' : 'Desktop'} audio permission failed:`, audioError)

        // EMERGENCY DEBUG: More detailed error analysis
        console.error('🔍 AUDIO PERMISSION ERROR DETAILS:', {
          name: (audioError as Error)?.name,
          message: (audioError as Error)?.message,
          stack: (audioError as Error)?.stack?.substring(0, 200),
          isIOSSafari,
          userAgent: navigator.userAgent.substring(0, 100)
        })
        results.permissionFlow = 'guided'

        if (audioError instanceof Error) {
          if (audioError.name === 'NotAllowedError') {
            results.error = 'Microphone access denied'
            results.userGuidance = isIOSSafari
              ? 'In iOS Safari: Settings > Safari > Camera & Microphone > Allow this website to access your microphone'
              : 'Click the microphone icon in your browser\'s address bar and select "Allow"'
          } else if (audioError.name === 'NotFoundError') {
            results.error = 'No microphone found'
            results.userGuidance = isIOSSafari
              ? 'Please ensure your iOS device has microphone access enabled'
              : 'Please connect a microphone and refresh the page'
          } else {
            results.error = 'Microphone access failed'
            results.userGuidance = isIOSSafari
              ? 'Please check iOS Safari microphone settings and try again'
              : 'Please check your microphone settings and try again'
          }
        }
      }

      // Force video permission with iOS Safari optimization
      try {
        // iOS SAFARI FIX: Use iOS-optimized video constraints
        const videoConstraints = isIOSSafari ? {
          width: { ideal: 640, max: 1280, min: 320 },
          height: { ideal: 480, max: 720, min: 240 },
          frameRate: { ideal: 24, max: 30, min: 15 }, // Conservative for iOS
          facingMode: 'user'
        } : {
          width: { ideal: 1920, min: 640 },
          height: { ideal: 1080, min: 480 },
          frameRate: { ideal: 60, min: 15 }
        }

        const audioConstraints = isIOSSafari ? {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 44100 },
          channelCount: { ideal: 1 }
        } : {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: { ideal: 48000 },
          channelCount: { ideal: 2 }
        }

        console.log(`📹 Requesting video with ${isIOSSafari ? 'iOS Safari' : 'desktop'} optimized constraints:`, videoConstraints)

        const videoStream = await this.getSmartMediaStream({
          audio: audioConstraints,
          video: videoConstraints
        })

        results.videoPermission = true
        console.log(`✅ ${isIOSSafari ? 'iOS Safari' : 'Desktop'} video permission granted`)

        // Keep stream cached for immediate use
        this.cachedVideoStream = videoStream

        // iOS SAFARI FIX: Validate video track for iOS compatibility
        if (isIOSSafari) {
          const videoTracks = videoStream.getVideoTracks()
          if (videoTracks.length > 0) {
            const settings = videoTracks[0]?.getSettings()
            console.log('📱 iOS Safari video track settings:', {
              width: settings.width,
              height: settings.height,
              frameRate: settings.frameRate,
              facingMode: settings.facingMode
            })

            // Test if video track is actually working
            if (settings.width && settings.height) {
              console.log('✅ iOS Safari: Video track dimensions confirmed')
            } else {
              console.warn('⚠️ iOS Safari: Video track has no dimensions')
            }
          }
        }

      } catch (videoError) {
        console.warn(`❌ ${isIOSSafari ? 'iOS Safari' : 'Desktop'} video permission failed:`, videoError)
        if (!results.error) results.permissionFlow = 'guided'
        if (videoError instanceof Error) {
          if (videoError.name === 'NotAllowedError') {
            // User denied or camera permission is blocked
            if (!results.error) {
              results.error = 'Camera access denied'
              results.userGuidance = 'Click the camera icon in your browser\'s address bar and select "Allow"'
            } else {
              // Both audio and video failed
              results.error = 'Camera and microphone access denied'
              results.userGuidance = 'Click the camera/microphone icons in your browser\'s address bar and select "Allow" for both'
            }
          } else if (videoError.name === 'NotFoundError') {
            if (!results.error) {
              results.error = 'No camera found'
              results.userGuidance = 'Please connect a camera and refresh the page'
            }
          } else if (videoError.name === 'NotReadableError') {
            if (!results.error) {
              results.error = 'Camera is busy'
              results.userGuidance = 'Close other applications using the camera and try again'
            }
          } else {
            if (!results.error) {
              results.error = 'Camera access failed'
              results.userGuidance = 'Please check your camera settings and try again'
            }
          }
        }
      }

      // Provide success messaging
      if (results.audioPermission && results.videoPermission) {
        console.log('🖥️ Desktop permissions successfully forced!')
      } else if (results.audioPermission) {
        console.log('🎤 Audio permission granted - ready for voice calls')
        if (!results.error) {
          results.userGuidance = 'Camera permission needed for video calls. Click the camera icon in your address bar to enable.'
        }
      } else if (results.videoPermission) {
        console.log('📹 Video permission granted')
      } else {
        if (!results.error) {
          results.error = 'No media permissions granted'
          results.userGuidance = 'Please allow camera and microphone access in your browser settings'
        }
      }

    } catch (error) {
      console.error('❌ Desktop permission forcing failed:', error)
      results.error = error instanceof Error ? error.message : 'Unknown permission error'
      results.userGuidance = 'Please check your browser settings and try again'
    }

    return results
  }

  /**
   * Initiate voice call to a peer using PeerJS MediaCall
   * Extracted from initiateVoiceCall() function in realP2PDemo.html line 4695
   */
  async initiateVoiceCall(
    targetPeerId: string,
    targetAlias: string,
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection,
    targetAvatar?: string | null
  ): Promise<boolean> {
    // CRITICAL DEBUG: Track alias flow at call initiation
    console.log(`🚨 VOICE CALL DEBUG - initiateVoiceCall parameters:`)
    console.log(`  targetPeerId: "${targetPeerId}" (${typeof targetPeerId})`)
    console.log(`  targetAlias: "${targetAlias}" (${typeof targetAlias})`)
    console.log(`  fromPeerId: "${fromPeerId}" (${typeof fromPeerId})`)
    console.log(`  fromAlias: "${fromAlias}" (${typeof fromAlias})`)
    console.log(`  connection.open: ${connection.open}`)

    if (!connection.open) {
      throw new Error('Connection not open')
    }

    if (this.callState.isInCall) {
      throw new Error('Already in a call')
    }

    if (!this._peer) {
      throw new Error('PeerJS instance not available')
    }

    try {
      // Phase 1: Initializing - Getting user media
      this.callState.callProgress = 'initializing'
      this.callState.callError = null
      this.emit('callStateChange', { ...this.callState })

      // Get user media for voice call with mobile-friendly constraints using smart acquisition
      const stream = await this.getSmartMediaStream({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // Mobile-specific optimizations
          sampleRate: { ideal: 48000 },
          channelCount: { ideal: 1 }
        },
        video: false
      })

      console.log('📞 Voice call media stream acquired')

      this.callState.localStream = stream
      this.callState.callType = 'voice'
      this.callState.activePeer = targetPeerId
      this.callState.activePeerAlias = targetAlias
      this.callState.activePeerAvatar = targetAvatar || null

      this.callState.isInCall = true
      this.callState.callStartTime = new Date()
      // Phase 2: Connecting - Setting up the call
      this.callState.callProgress = 'connecting'
      this.emit('callStateChange', { ...this.callState })

      // Send call request via data connection first
      const callData: CallRequest = {
        type: 'voice-call-request',
        from: fromPeerId,
        fromAlias,
        timestamp: Date.now()
      }

      connection.send(callData)

      // CRITICAL FIX: Ensure alias is properly included in metadata for call identification
      const isDesktopCaller = !(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
      const optimizedMetadata = {
        type: 'voice',
        // FIXED: Always include full alias for proper identification (truncate only if extremely long)
        fromAlias: fromAlias.length > 50 ? fromAlias.substring(0, 47) + '...' : fromAlias,
        fromPeerId, // Include peer ID as backup
        fromDevice: isDesktopCaller ? 'desktop' : 'mobile', // Device type for optimization
        timestamp: Date.now()
      }

      // CRITICAL DEBUG: Track metadata being sent
      console.log(`🚨 VOICE CALL DEBUG - optimizedMetadata being sent:`)
      console.log(`  metadata.fromAlias: "${optimizedMetadata.fromAlias}" (original: "${fromAlias}")`)
      console.log(`  metadata.fromPeerId: "${optimizedMetadata.fromPeerId}"`)
      console.log(`  metadata.type: "${optimizedMetadata.type}"`)
      console.log(`  metadata.fromDevice: "${optimizedMetadata.fromDevice}"`)
      console.log(`  Full metadata object:`, optimizedMetadata)

      // Initiate PeerJS media call
      console.log(`🚨 DEBUG: Desktop calling with stream tracks:`, stream.getTracks().length)
      stream.getTracks().forEach((track, i) => {
        console.log(`🚨 DEBUG: Desktop call track ${i}:`, track.kind, track.enabled, track.readyState)
      })
      this._currentMediaCall = this._peer.call(targetPeerId, stream, {
        metadata: optimizedMetadata
      })

      // Phase 3: Ringing - Call sent, waiting for answer
      this.callState.callProgress = 'ringing'
      this.emit('callStateChange', { ...this.callState })

      this._currentMediaCall.on('stream', (remoteStream: MediaStream) => {
        console.log('📞 DESKTOP CALLER: Received remote voice stream from mobile')
        console.log('🚨 DEBUG: Desktop caller remote stream tracks:', remoteStream.getTracks().length)
        this.callState.remoteStream = remoteStream

        // Phase 4: Connected - Call established successfully
        this.callState.callProgress = 'connected'

        // CALL STATE SYNC FIX: Immediate state notification for responsive UI
        this.emit('callStateChange', { ...this.callState })
        this.notifyCallListeners()

        // CRITICAL FIX: Handle remote audio playback for voice calls
        this.handleRemoteStreamPlayback(remoteStream, 'voice')

        this.emit('remoteStream', remoteStream)

        console.log('📞 Voice call fully connected - all listeners notified')
      })

      this._currentMediaCall.on('close', () => {
        console.log('📞 Voice call ended by peer')
        this.endCall()
      })

      this._currentMediaCall.on('error', (error: Error) => {
        console.error('Voice call error:', error)
        // Set failed state with error details
        this.callState.callProgress = 'failed'
        this.callState.callError = error.message
        this.emit('callStateChange', { ...this.callState })
        toastManager.error('Voice call failed', TOAST_GROUPS.CALL)
        this.endCall(undefined, false)
      })

      console.log(`📞 Voice call initiated to ${targetAlias}`)
      toastManager.success(`Calling ${targetAlias}...`, TOAST_GROUPS.CALL)

      this.notifyCallListeners()
      return true

    } catch (error) {
      console.error('Cannot access microphone:', error)
      const errorMsg = error instanceof Error && error.name === 'NotAllowedError'
        ? 'Microphone access denied. Please allow microphone permissions and try again.'
        : 'Cannot access microphone. Please check your device settings.'

      // Set failed state with error details
      this.callState.callProgress = 'failed'
      this.callState.callError = errorMsg
      this.emit('callStateChange', { ...this.callState })

      toastManager.error(errorMsg, TOAST_GROUPS.CALL)
      this.resetCallState()
      return false
    }
  }

  /**
   * Initiate video call to a peer using PeerJS MediaCall
   * Extracted from initiateVideoCall() function in realP2PDemo.html line 4725
   */
  async initiateVideoCall(
    targetPeerId: string,
    targetAlias: string,
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection,
    localVideoElement?: HTMLVideoElement,
    targetAvatar?: string | null
  ): Promise<boolean> {
    // AVATAR DEBUG: Track avatar data flow
    console.log(`🖼️ AVATAR DEBUG - initiateVideoCall parameters:`)
    console.log(`  targetAvatar: "${targetAvatar}" (${typeof targetAvatar})`)
    console.log(`  targetAvatar length: ${targetAvatar?.length || 'null'}`)
    if (!connection.open) {
      throw new Error('Connection not open')
    }

    if (this.callState.isInCall) {
      throw new Error('Already in a call')
    }

    if (!this._peer) {
      throw new Error('PeerJS instance not available')
    }

    try {
      // Phase 1: Initializing - Getting user media
      this.callState.callProgress = 'initializing'
      this.callState.callError = null
      this.emit('callStateChange', { ...this.callState })

      // CRITICAL FIX: Enhanced video call constraints for B(3) Desktop → Mobile compatibility
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const isCrossPlatformCall = !isMobile // Desktop caller - optimize for mobile receiver

      console.log(`🎯 Video call setup: caller=${isMobile ? 'mobile' : 'desktop'}, optimizeForMobile=${isCrossPlatformCall}`)

      const videoConstraints = isMobile ? {
        // Mobile caller constraints
        width: { ideal: 640, max: 1280 },
        height: { ideal: 480, max: 720 },
        frameRate: { ideal: 24, max: 30 },
        facingMode: 'user'
      } : isCrossPlatformCall ? {
        // CRITICAL FIX: Desktop → Mobile optimized constraints
        width: { ideal: 1280, max: 1280, min: 640 }, // Cap resolution for mobile compatibility
        height: { ideal: 720, max: 720, min: 480 },
        frameRate: { ideal: 30, max: 30, min: 15 }, // Cap frame rate for mobile processing
        facingMode: 'user'
      } : {
        // Desktop → Desktop high quality
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        frameRate: { ideal: 30, max: 60 }
      }

      const stream = await this.getSmartMediaStream({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          // CRITICAL FIX: Mobile-compatible audio for cross-platform calls
          sampleRate: isCrossPlatformCall ? { ideal: 48000, max: 48000 } : { ideal: 48000 },
          channelCount: isCrossPlatformCall ? { ideal: 1, max: 2 } : { ideal: 2 } // Mono for mobile compatibility
        },
        video: videoConstraints
      })

      this.callState.localStream = stream
      this.callState.callType = 'video'
      this.callState.activePeer = targetPeerId
      this.callState.activePeerAlias = targetAlias
      this.callState.activePeerAvatar = targetAvatar || null

      // AVATAR DEBUG: Confirm setting
      console.log(`🖼️ AVATAR DEBUG - Call state set:`)
      console.log(`  activePeerAvatar: "${this.callState.activePeerAvatar}"`)
      console.log(`  activePeerAlias: "${this.callState.activePeerAlias}"`)
      this.callState.isInCall = true
      this.callState.callStartTime = new Date()
      // Phase 2: Connecting - Setting up the call
      this.callState.callProgress = 'connecting'
      this.emit('callStateChange', { ...this.callState })

      // Set local video if element provided
      if (localVideoElement && stream) {
        localVideoElement.srcObject = stream
        localVideoElement.muted = true // Prevent feedback
        localVideoElement.play().catch(e => console.log('Local video play failed:', e))
      }

      // Send call request via data connection first
      const callData: CallRequest = {
        type: 'video-call-request',
        from: fromPeerId,
        fromAlias,
        timestamp: Date.now()
      }

      connection.send(callData)

      // CRITICAL FIX: Ensure alias is properly included in metadata for call identification
      const isDesktopCaller = !(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
      const optimizedMetadata = {
        type: 'video',
        // FIXED: Always include full alias for proper identification (truncate only if extremely long)
        fromAlias: fromAlias.length > 50 ? fromAlias.substring(0, 47) + '...' : fromAlias,
        fromPeerId, // Include peer ID as backup
        fromDevice: isDesktopCaller ? 'desktop' : 'mobile', // CRITICAL: Device type for cross-platform optimization
        timestamp: Date.now(),
        // B(3) optimization hints
        crossPlatformOptimized: isDesktopCaller // Desktop callers should use mobile-compatible streams
      }

      // Initiate PeerJS media call
      this._currentMediaCall = this._peer.call(targetPeerId, stream, {
        metadata: optimizedMetadata
      })

      // Phase 3: Ringing - Call sent, waiting for answer
      this.callState.callProgress = 'ringing'
      this.emit('callStateChange', { ...this.callState })

      this._currentMediaCall.on('stream', (remoteStream: MediaStream) => {
        console.log('📹 Received remote video stream')
        this.callState.remoteStream = remoteStream

        // Phase 4: Connected - Call established successfully
        this.callState.callProgress = 'connected'
        this.emit('callStateChange', { ...this.callState })

        // CRITICAL FIX: Handle remote stream playback for video calls
        this.handleRemoteStreamPlayback(remoteStream, 'video')

        this.emit('remoteStream', remoteStream)
        this.notifyCallListeners()
      })

      this._currentMediaCall.on('close', () => {
        console.log('📹 Video call ended by peer')
        this.endCall()
      })

      this._currentMediaCall.on('error', (error: Error) => {
        console.error('Video call error:', error)
        // Set failed state with error details
        this.callState.callProgress = 'failed'
        this.callState.callError = error.message
        this.emit('callStateChange', { ...this.callState })
        toastManager.error('Video call failed', TOAST_GROUPS.CALL)
        this.endCall(undefined, false)
      })

      console.log(`📹 Video call initiated to ${targetAlias}`)
      toastManager.success(`Video calling ${targetAlias}...`, TOAST_GROUPS.CALL)

      this.notifyCallListeners()
      return true

    } catch (error) {
      console.error('Cannot access camera/microphone:', error)
      const errorMsg = error instanceof Error && error.name === 'NotAllowedError'
        ? 'Camera/microphone access denied. Please allow permissions and try again.'
        : 'Cannot access camera/microphone. Please check your device settings.'

      // Set failed state with error details
      this.callState.callProgress = 'failed'
      this.callState.callError = errorMsg
      this.emit('callStateChange', { ...this.callState })

      toastManager.error(errorMsg, TOAST_GROUPS.CALL)
      this.resetCallState()
      return false
    }
  }

  /**
   * Start screen sharing
   * Extracted from shareScreen() function in realP2PDemo.html line 4866
   */
  async startScreenShare(
    _targetPeerId: string,
    targetAlias: string,
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection
  ): Promise<boolean> {
    if (!connection.open) {
      throw new Error('Connection not open')
    }

    try {
      // Get display media for screen sharing
      const stream = await navigator.mediaDevices.getDisplayMedia({ 
        video: true, 
        audio: true 
      })

      this.screenStream = stream

      const shareData: ScreenShareStart = {
        type: 'screen-share-start',
        from: fromPeerId,
        fromAlias,
        timestamp: Date.now()
      }

      connection.send(shareData)

      console.log(`🖥️ Screen sharing initiated with ${targetAlias}`)
      toastManager.success('Screen sharing started', TOAST_GROUPS.CALL)

      // Handle stream end
      const videoTrack = stream.getVideoTracks()[0]
      if (videoTrack) {
        videoTrack.onended = () => {
          const endData: ScreenShareEnd = {
            type: 'screen-share-end',
            from: fromPeerId,
            timestamp: Date.now()
          }
          connection.send(endData)
          console.log('🖥️ Screen sharing ended')
          toastManager.info('Screen sharing ended', TOAST_GROUPS.CALL, { icon: '🖥️' })
          this.screenStream = null
        }
      }

      return true

    } catch (error) {
      console.error('Screen sharing failed:', error)
      toastManager.error('Screen sharing failed', TOAST_GROUPS.CALL)
      return false
    }
  }

  /**
   * Accept incoming call with PeerJS media call handling
   */
  async acceptCall(
    callType: 'voice' | 'video',
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection,
    localVideoElement?: HTMLVideoElement,
    incomingMediaCall?: any,
    callerAvatar?: string | null
  ): Promise<boolean> {
    try {
      // CRITICAL FIX: Enhanced call acceptance with cross-platform optimization
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const isReceivingFromDesktop = incomingMediaCall?.metadata?.fromDevice === 'desktop'

      console.log(`🎯 Call acceptance: receiver=${isMobile ? 'mobile' : 'desktop'}, fromDesktop=${isReceivingFromDesktop}`)
      console.log(`🚨 DEBUG - metadata.fromDevice: "${incomingMediaCall?.metadata?.fromDevice}"`)

      const constraints = callType === 'video'
        ? {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              // Mobile-optimized audio for B(3) compatibility
              sampleRate: isMobile ? { ideal: 48000 } : { ideal: 48000 },
              channelCount: isMobile ? { ideal: 1 } : { ideal: 2 }
            },
            video: isMobile ? {
              // CRITICAL FIX: Mobile receiver optimized for desktop streams
              width: { ideal: 640, max: 1280, min: 320 },
              height: { ideal: 480, max: 720, min: 240 },
              frameRate: { ideal: 24, max: 30, min: 15 }, // Conservative for mobile
              facingMode: 'user'
            } : {
              // Desktop receiver can handle higher quality
              width: { ideal: 1280, max: 1920 },
              height: { ideal: 720, max: 1080 },
              frameRate: { ideal: 30, max: 60 }
            }
          }
        : {
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              sampleRate: { ideal: 48000 },
              channelCount: isMobile ? { ideal: 1 } : { ideal: 2 }
            },
            video: false
          }

      const stream = await this.getSmartMediaStream(constraints)

      this.callState.localStream = stream
      this.callState.callType = callType
      this.callState.activePeer = fromPeerId
      this.callState.activePeerAlias = fromAlias
      this.callState.activePeerAvatar = callerAvatar || null
      this.callState.isInCall = true
      this.callState.callStartTime = new Date()

      // CALL STATE SYNC FIX: Set connecting state immediately when accepting
      this.callState.callProgress = 'connecting'
      this.callState.callError = null

      // Set local video if element provided and it's a video call
      if (localVideoElement && callType === 'video' && stream) {
        localVideoElement.srcObject = stream
        localVideoElement.muted = true // Prevent feedback
        localVideoElement.play().catch(e => console.log('Local video play failed:', e))
      }

      // Handle incoming PeerJS media call
      if (incomingMediaCall) {
        this._currentMediaCall = incomingMediaCall
        
        // Answer the PeerJS call with our stream
        console.log(`🚨 DEBUG: Mobile answering call with stream tracks:`, stream.getTracks().length)
        stream.getTracks().forEach((track, i) => {
          console.log(`🚨 DEBUG: Mobile answer track ${i}:`, track.kind, track.enabled, track.readyState)
        })
        this._currentMediaCall.answer(stream)
        
        this._currentMediaCall.on('stream', (remoteStream: MediaStream) => {
          console.log(`📞 MOBILE RECEIVER: Received remote ${callType} stream from desktop`)
          console.log(`🚨 DEBUG: Mobile receiver remote stream tracks:`, remoteStream.getTracks().length)
          this.callState.remoteStream = remoteStream

          // CALL STATE SYNC FIX: Update call progress to connected when stream is received
          this.callState.callProgress = 'connected'

          // CALL STATE SYNC FIX: Immediate state notification for responsive UI
          this.emit('callStateChange', { ...this.callState })
          this.notifyCallListeners()

          // CRITICAL FIX: Handle remote stream playback
          this.handleRemoteStreamPlayback(remoteStream, callType)

          this.emit('remoteStream', remoteStream)

          console.log(`📞 ${callType} call fully connected - all listeners notified`)
        })

        this._currentMediaCall.on('close', () => {
          console.log(`📞 ${callType} call ended by peer`)
          this.endCall()
        })

        this._currentMediaCall.on('error', (error: Error) => {
          console.error(`${callType} call error:`, error)
          toastManager.error(`${callType} call failed`, TOAST_GROUPS.CALL)
          this.endCall(undefined, false)
        })
      }

      const responseData: CallResponse = {
        type: callType === 'video' ? 'video-call-response' : 'voice-call-response',
        from: this.callState.activePeer || '',
        fromAlias: this.callState.activePeerAlias || '',
        accepted: true,
        timestamp: Date.now()
      }

      connection.send(responseData)

      console.log(`✅ ${callType} call accepted from ${fromAlias}`)
      toastManager.success(`${callType === 'video' ? 'Video' : 'Voice'} call connected`, TOAST_GROUPS.CALL)

      this.notifyCallListeners()
      return true

    } catch (error) {
      console.error(`Cannot access ${callType === 'video' ? 'camera/' : ''}microphone:`, error)
      const errorMsg = error instanceof Error && error.name === 'NotAllowedError' 
        ? `${callType === 'video' ? 'Camera/' : ''}Microphone access denied. Please allow permissions and try again.`
        : `Cannot access ${callType === 'video' ? 'camera/' : ''}microphone. Please check your device settings.`
      toastManager.error(errorMsg, TOAST_GROUPS.CALL)
      
      // Send rejection
      const responseData: CallResponse = {
        type: callType === 'video' ? 'video-call-response' : 'voice-call-response',
        from: fromPeerId,
        fromAlias,
        accepted: false,
        timestamp: Date.now()
      }
      connection.send(responseData)
      
      // Reject PeerJS call if provided
      if (incomingMediaCall) {
        incomingMediaCall.close()
      }
      
      return false
    }
  }

  /**
   * Reject incoming call
   */
  rejectCall(
    callType: 'voice' | 'video',
    fromPeerId: string,
    fromAlias: string,
    connection: DataConnection
  ): void {
    const responseData: CallResponse = {
      type: callType === 'video' ? 'video-call-response' : 'voice-call-response',
      from: fromPeerId,
      fromAlias,
      accepted: false,
      timestamp: Date.now()
    }

    connection.send(responseData)
    console.log(`❌ ${callType} call rejected from ${fromAlias}`)
    toastManager.error(`Call rejected`, TOAST_GROUPS.CALL)
  }

  /**
   * VOICE-FIRST ARCHITECTURE: Upgrade active voice call to video call
   * Adds video stream to ongoing voice call without restarting the call
   */
  async upgradeVoiceCallToVideo(): Promise<boolean> {
    // Validate current call state
    if (!this.callState.isInCall || this.callState.callType !== 'voice') {
      throw new Error('Can only upgrade active voice calls to video')
    }

    if (!this._currentMediaCall) {
      throw new Error('No active media call to upgrade')
    }

    if (!this.callState.localStream) {
      throw new Error('No local stream available for upgrade')
    }

    try {
      console.log('🎯 Voice-First: Upgrading voice call to video')

      // Get video stream while preserving audio
      const videoStream = await this.getSmartMediaStream({
        audio: true, // Keep existing audio
        video: {
          width: { ideal: 1280, min: 640 },
          height: { ideal: 720, min: 480 },
          frameRate: { ideal: 30, min: 15 },
          facingMode: 'user'
        }
      })

      console.log('✅ Video stream acquired for upgrade')

      // Get the video track from the new stream
      const videoTrack = videoStream.getVideoTracks()[0]
      if (!videoTrack) {
        throw new Error('No video track in acquired stream')
      }

      // Add video track to existing call via peer connection
      if (this._currentMediaCall.peerConnection) {
        // Add the video track to the existing peer connection
        this._currentMediaCall.peerConnection.addTrack(videoTrack, videoStream)
        console.log('✅ Video track added to existing peer connection')
      } else {
        throw new Error('No peer connection available for track addition')
      }

      // Update call state to video type
      this.callState.callType = 'video'
      this.callState.localStream = videoStream

      // Emit call state change
      this.emit('callStateChange', { ...this.callState })

      // Emit local video stream for UI
      this.emit('localVideoStream', videoStream)

      console.log('🎯 Voice-First: Successfully upgraded to video call')
      toastManager.success('Video enabled', TOAST_GROUPS.CALL)

      return true

    } catch (error) {
      console.error('❌ Voice-First: Failed to upgrade to video:', error)

      const errorMsg = error instanceof Error && error.name === 'NotAllowedError'
        ? 'Camera access denied. Please allow camera permissions and try again.'
        : 'Failed to enable video. Please check your camera settings.'

      toastManager.error(errorMsg, TOAST_GROUPS.CALL)
      return false
    }
  }

  /**
   * VOICE-FIRST ARCHITECTURE: Downgrade video call to voice-only
   * Removes video tracks while keeping audio active
   */
  async downgradeVideoCallToVoice(): Promise<boolean> {
    // Validate current call state
    if (!this.callState.isInCall || this.callState.callType !== 'video') {
      throw new Error('Can only downgrade active video calls to voice')
    }

    if (!this._currentMediaCall) {
      throw new Error('No active media call to downgrade')
    }

    try {
      console.log('🎯 Voice-First: Downgrading video call to voice')

      // Stop and remove video tracks from local stream
      if (this.callState.localStream) {
        const videoTracks = this.callState.localStream.getVideoTracks()
        videoTracks.forEach(track => {
          track.stop()
          console.log('Stopped local video track')
        })

        // Remove video tracks from peer connection
        if (this._currentMediaCall.peerConnection) {
          const senders = this._currentMediaCall.peerConnection.getSenders()
          senders.forEach((sender: RTCRtpSender) => {
            if (sender.track && sender.track.kind === 'video') {
              this._currentMediaCall.peerConnection.removeTrack(sender)
              console.log('Removed video track from peer connection')
            }
          })
        }

        // Create new audio-only stream
        const audioStream = await this.getSmartMediaStream({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          },
          video: false
        })

        this.callState.localStream = audioStream
      }

      // Update call state to voice type
      this.callState.callType = 'voice'

      // Emit call state change
      this.emit('callStateChange', { ...this.callState })

      console.log('🎯 Voice-First: Successfully downgraded to voice call')
      toastManager.success('Video disabled', TOAST_GROUPS.CALL)

      return true

    } catch (error) {
      console.error('❌ Voice-First: Failed to downgrade to voice:', error)
      toastManager.error('Failed to disable video', TOAST_GROUPS.CALL)
      return false
    }
  }

  /**
   * End current call including PeerJS media call
   */
  endCall(connection?: DataConnection, showNotification: boolean = true): void {
    // CRITICAL FIX: Only proceed if we were actually in a call
    // This prevents premature "Call ended" notifications during network cleanup
    if (!this.callState.isInCall) {
      // Silently clean up resources without notifications
      console.log('📞 endCall called but no active call - cleaning up resources silently')
      this.resetCallState()
      return
    }

    // Additional safety check: Ensure we have valid call data
    if (!this.callState.activePeer || !this.callState.callType) {
      console.log('📞 endCall called with incomplete call state - cleaning up silently')
      this.resetCallState()
      return
    }

    // Stop local stream tracks
    if (this.callState.localStream) {
      this.callState.localStream.getTracks().forEach(track => {
        track.stop()
        console.log(`Stopped ${track.kind} track`)
      })
    }

    // Stop remote stream tracks
    if (this.callState.remoteStream) {
      this.callState.remoteStream.getTracks().forEach(track => {
        track.stop()
      })
    }

    // Close PeerJS media call
    if (this._currentMediaCall) {
      this._currentMediaCall.close()
      this._currentMediaCall = null
    }

    // Notify peer via data connection
    if (connection && this.callState.activePeer) {
      try {
        connection.send({
          type: 'call-ended',
          from: this.callState.activePeer,
          timestamp: Date.now()
        })
      } catch (error) {
        console.warn('Failed to send call-ended message:', error)
      }
    }

    // Only show notification for legitimate call endings (not cleanup operations)
    if (showNotification) {
      console.log('📞 Call ended and all resources cleaned up')
      toastManager.info('Call ended', TOAST_GROUPS.CALL, { icon: '📞' })
    } else {
      console.log('📞 Call resources cleaned up (no notification - cleanup operation)')
    }

    this.resetCallState()
    this.notifyCallListeners()
  }

  /**
   * Stop screen sharing
   */
  stopScreenShare(): void {
    if (this.screenStream) {
      this.screenStream.getTracks().forEach(track => track.stop())
      this.screenStream = null
      console.log('🖥️ Screen sharing stopped')
      toastManager.info('Screen sharing stopped', TOAST_GROUPS.CALL, { icon: '🖥️' })
    }
  }

  /**
   * Handle incoming call request
   */
  handleCallRequest(data: CallRequest, _fromPeerId: string): void {
    if (this.callState.isInCall) {
      // Already in a call, reject automatically
      console.log(`📞 Call from ${data.fromAlias} rejected - already in call`)
      return
    }

    const callType = data.type === 'video-call-request' ? 'video' : 'voice'
    console.log(`📞 Incoming ${callType} call from ${data.fromAlias}`)

    // Play incoming call sound based on call type
    notificationSoundService.playIncomingCallSound(callType).catch(error => {
      console.warn(`🔊 Failed to play incoming ${callType} call sound:`, error)
    })

    // This would typically show a call notification UI
    // For now, just log and could auto-accept or show prompt
    toastManager.info(`Incoming ${callType} call from ${data.fromAlias}`, TOAST_GROUPS.CALL, { duration: 15000, icon: callType === 'video' ? '📹' : '📞' })
  }

  /**
   * Handle call response
   * CALL STATE SYNC FIX: Enhanced call response handling for better state synchronization
   */
  handleCallResponse(data: CallResponse, _fromPeerId: string): void {
    if (data.accepted) {
      console.log(`✅ Call accepted by ${data.fromAlias}`)

      // CALL STATE SYNC FIX: Update call progress when acceptance is received
      if (this.callState.callProgress === 'ringing') {
        this.callState.callProgress = 'connecting'
        console.log('📞 Call state updated to "connecting" after acceptance')

        // Notify UI immediately about acceptance
        this.emit('callStateChange', { ...this.callState })
        this.notifyCallListeners()
      }

      toastManager.success(`Call accepted by ${data.fromAlias}`, TOAST_GROUPS.CALL)
    } else {
      console.log(`❌ Call rejected by ${data.fromAlias}`)
      toastManager.error(`Call rejected by ${data.fromAlias}`, TOAST_GROUPS.CALL)

      // CALL STATE SYNC FIX: Set failed state before reset for proper UI feedback
      this.callState.callProgress = 'failed'
      this.callState.callError = 'Call rejected'
      this.emit('callStateChange', { ...this.callState })

      // Short delay to allow UI to show rejection state
      setTimeout(() => {
        this.resetCallState()
        this.notifyCallListeners()
      }, 1000)
    }
  }

  /**
   * Handle screen share start
   */
  handleScreenShareStart(data: ScreenShareStart, _fromPeerId: string): void {
    console.log(`🖥️ Screen sharing started by ${data.fromAlias}`)
    toastManager.info(`${data.fromAlias} is sharing their screen`, TOAST_GROUPS.CALL, { icon: '🖥️' })
  }

  /**
   * Handle screen share end
   */
  handleScreenShareEnd(_data: ScreenShareEnd, _fromPeerId: string): void {
    console.log(`🖥️ Screen sharing ended`)
    toastManager.info('Screen sharing ended', TOAST_GROUPS.CALL, { icon: '🖥️' })
  }

  /**
   * Handle call ended message from peer
   * CRITICAL FIX: Only show call-ended notification if we were actually in a call with this peer
   */
  handleCallEnded(_data: { type: string; from: string; timestamp: number }, fromPeerId: string): void {
    // CRITICAL LOGIC: Only process if we were actually in a call with this specific peer
    if (!this.callState.isInCall || this.callState.activePeer !== fromPeerId) {
      console.log(`📞 Ignoring call-ended from ${fromPeerId} - no active call with this peer`)
      return
    }

    console.log(`📞 Call ended by ${this.callState.activePeerAlias || fromPeerId}`)
    toastManager.info('Call ended by peer', TOAST_GROUPS.CALL, { icon: '📞' })

    // Clean up the call state
    this.endCall(undefined, false) // Don't show duplicate notification
  }

  /**
   * Handle P2P data (router method)
   */
  handleP2PData(data: unknown, fromPeerId: string): boolean {
    if (typeof data !== 'object' || !data) {
      return false
    }

    const typedData = data as { type?: string }

    switch (typedData.type) {
      case 'voice-call-request':
      case 'video-call-request':
        this.handleCallRequest(typedData as CallRequest, fromPeerId)
        return true

      case 'voice-call-response':
      case 'video-call-response':
        this.handleCallResponse(typedData as CallResponse, fromPeerId)
        return true

      case 'screen-share-start':
        this.handleScreenShareStart(typedData as ScreenShareStart, fromPeerId)
        return true

      case 'screen-share-end':
        this.handleScreenShareEnd(typedData as ScreenShareEnd, fromPeerId)
        return true

      case 'call-ended':
        this.handleCallEnded(typedData as { type: string; from: string; timestamp: number }, fromPeerId)
        return true

      default:
        return false
    }
  }

  /**
   * Get current call state
   */
  getCallState(): CallState {
    return { ...this.callState }
  }

  /**
   * CONTEXTUAL CALL ENHANCEMENT: Check if currently navigating during a call
   */
  getIsNavigatingDuringCall(): boolean {
    return this.isNavigatingDuringCall
  }

  /**
   * CONTEXTUAL CALL ENHANCEMENT: Get call state before navigation (for recovery)
   */
  getCallStateBeforeNavigation(): CallState | null {
    return this.callStateBeforeNavigation
  }

  /**
   * Add call state listener
   */
  onCallStateChange(listener: (state: CallState) => void): () => void {
    this.callListeners.push(listener)
    return () => {
      const index = this.callListeners.indexOf(listener)
      if (index > -1) {
        this.callListeners.splice(index, 1)
      }
    }
  }

  /**
   * Check if device has media capabilities and current permission states
   */
  async checkMediaCapabilities(): Promise<{
    hasAudio: boolean
    hasVideo: boolean
    hasScreenShare: boolean
    permissions: {
      microphone: PermissionState | 'unknown'
      camera: PermissionState | 'unknown'
    }
  }> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const hasAudio = devices.some(device => device.kind === 'audioinput')
      const hasVideo = devices.some(device => device.kind === 'videoinput')
      const hasScreenShare = 'getDisplayMedia' in navigator.mediaDevices

      return {
        hasAudio,
        hasVideo,
        hasScreenShare,
        permissions: {
          microphone: this.permissionStates.microphone,
          camera: this.permissionStates.camera
        }
      }
    } catch (error) {
      console.warn('Cannot check media capabilities:', error)
      return {
        hasAudio: false,
        hasVideo: false,
        hasScreenShare: false,
        permissions: {
          microphone: 'unknown',
          camera: 'unknown'
        }
      }
    }
  }

  /**
   * Request microphone permissions proactively (useful for UI feedback)
   */
  async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await this.getSmartMediaStream({
        audio: true,
        video: false
      })

      console.log('✅ Microphone permission granted proactively')

      // Keep the stream cached for immediate reuse
      this.cachedAudioStream = stream

      return true
    } catch (error) {
      console.error('❌ Microphone permission request failed:', error)
      return false
    }
  }

  /**
   * Request camera and microphone permissions proactively (useful for UI feedback)
   */
  async requestCameraPermission(): Promise<boolean> {
    try {
      const stream = await this.getSmartMediaStream({
        audio: true,
        video: true
      })

      console.log('✅ Camera and microphone permissions granted proactively')

      // Keep the stream cached for immediate reuse
      this.cachedVideoStream = stream

      return true
    } catch (error) {
      console.error('❌ Camera permission request failed:', error)
      return false
    }
  }

  /**
   * Force refresh cached streams (useful when user changes device settings)
   */
  refreshMediaStreams(): void {
    console.log('🔄 Refreshing cached media streams')
    this.cleanupCachedStreams()
    // Reset permission states to trigger fresh checks
    this.resetPermissionStates()
    this.initializePermissionMonitoring()
  }

  /**
   * SURGICAL FIX: Reset permission states for retry attempts
   * This allows users to retry permissions without page refresh
   */
  resetPermissionStates(): void {
    console.log('🔄 Resetting permission states for retry')
    this.permissionStates.microphone = 'unknown'
    this.permissionStates.camera = 'unknown'
  }

  /**
   * PROACTIVE PERMISSIONS: Request permissions immediately on app load for desktop users
   * This prevents "mic and camera not working" issues by establishing permissions early
   */
  async requestProactivePermissions(): Promise<{
    success: boolean
    audioGranted: boolean
    videoGranted: boolean
    error?: string
    shouldShowManualGuidance?: boolean
  }> {
    const isDesktop = !(/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent))
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && !!(window as any).MSStream === false

    // Only run proactive permissions for desktop and iOS Safari
    if (!isDesktop && !isIOSSafari) {
      return { success: false, audioGranted: false, videoGranted: false, error: 'Mobile device - permissions requested on demand' }
    }

    console.log(`🚀 PROACTIVE PERMISSIONS: Starting for ${isIOSSafari ? 'iOS Safari' : 'desktop'}`)

    try {
      const permissionResult = await this.forceDesktopPermissions()

      return {
        success: permissionResult.audioPermission || permissionResult.videoPermission,
        audioGranted: permissionResult.audioPermission,
        videoGranted: permissionResult.videoPermission,
        error: permissionResult.error || undefined,
        shouldShowManualGuidance: permissionResult.permissionFlow === 'guided'
      }
    } catch (error) {
      console.error('🚀 PROACTIVE PERMISSIONS: Failed:', error)
      return {
        success: false,
        audioGranted: false,
        videoGranted: false,
        error: error instanceof Error ? error.message : 'Permission request failed',
        shouldShowManualGuidance: true
      }
    }
  }

  /**
   * NEW: Request permissions step by step with better user experience
   * This method requests audio first, then video, providing clear feedback at each step
   */
  async requestPermissionsStepByStep(): Promise<{
    audioGranted: boolean
    videoGranted: boolean
    currentStep: 'audio' | 'video' | 'complete'
    instructions?: string
    error?: string
  }> {
    const result: {
      audioGranted: boolean
      videoGranted: boolean
      currentStep: 'audio' | 'video' | 'complete'
      instructions?: string
      error?: string
    } = {
      audioGranted: false,
      videoGranted: false,
      currentStep: 'audio'
    }

    try {
      // Step 1: Request audio permission
      console.log('🎤 Step 1: Requesting microphone permission...')
      result.instructions = 'Please allow microphone access when prompted'

      try {
        const audioStream = await this.getSmartMediaStream({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          },
          video: false
        })

        result.audioGranted = true
        this.cachedAudioStream = audioStream
        console.log('✅ Microphone permission granted')
      } catch (audioError) {
        console.warn('❌ Microphone permission denied:', audioError)
        result.error = 'Microphone permission denied'
        return result
      }

      // Step 2: Request video permission
      result.currentStep = 'video'
      result.instructions = 'Please allow camera access when prompted'
      console.log('📹 Step 2: Requesting camera permission...')

      try {
        const videoStream = await this.getSmartMediaStream({
          audio: true,
          video: {
            width: { ideal: 1280, min: 640 },
            height: { ideal: 720, min: 480 },
            frameRate: { ideal: 30, min: 15 }
          }
        })

        result.videoGranted = true
        this.cachedVideoStream = videoStream
        console.log('✅ Camera permission granted')
      } catch (videoError) {
        console.warn('❌ Camera permission denied:', videoError)
        // Audio is still granted, so this is partial success
        result.instructions = 'Camera access denied. You can still make voice calls.'
      }

      result.currentStep = 'complete'

      if (result.audioGranted && result.videoGranted) {
        result.instructions = 'All permissions granted! Ready for voice and video calls.'
      } else if (result.audioGranted) {
        result.instructions = 'Microphone ready! You can make voice calls. Grant camera access for video calls.'
      }

    } catch (error) {
      console.error('❌ Permission request failed:', error)
      result.error = error instanceof Error ? error.message : 'Permission request failed'
    }

    return result
  }

  /**
   * Reset call state
   */
  private resetCallState(): void {
    this.callState = {
      isInCall: false,
      callType: null,
      localStream: null,
      remoteStream: null,
      activePeer: null,
      activePeerAlias: null,
      activePeerAvatar: null,
      callStartTime: null,
      callProgress: 'idle',
      callError: null
    }
  }

  /**
   * Clean up cached streams (call when permissions are revoked or streams are no longer needed)
   */
  private cleanupCachedStreams(): void {
    if (this.cachedAudioStream) {
      this.cachedAudioStream.getTracks().forEach(track => {
        track.stop()
        console.log('Stopped cached audio track')
      })
      this.cachedAudioStream = null
    }

    if (this.cachedVideoStream) {
      this.cachedVideoStream.getTracks().forEach(track => {
        track.stop()
        console.log('Stopped cached video track')
      })
      this.cachedVideoStream = null
    }
  }

  /**
   * Notify call state listeners
   */
  private notifyCallListeners(): void {
    this.callListeners.forEach(listener => {
      listener(this.getCallState())
    })
  }

  /**
   * Get current peer ID
   */
  getPeerId(): string | null {
    return this._peer?.id || this._localPeerId || null
  }

  /**
   * Get current alias
   */
  getAlias(): string | null {
    return this._localAlias || null
  }

  /**
   * Update local peer information and PeerJS instance
   */
  updateLocalPeer(peerId: string, alias: string, peer?: any, aliasRegistry?: any): void {
    this._localPeerId = peerId
    this._localAlias = alias
    if (peer) {
      this._peer = peer
    }
    // MOBILE FIX: Update alias registry reference for enhanced mobile resolution
    if (aliasRegistry) {
      this._aliasRegistry = aliasRegistry
      console.log('✅ MediaService: Alias registry updated for mobile call resolution')
    }
  }

  /**
   * Handle incoming PeerJS media call with enhanced mobile alias resolution
   * CRITICAL FIX: Comprehensive data validation and recovery
   */
  handleIncomingMediaCall(mediaCall: any): void {
    // CRITICAL VALIDATION: Ensure we have a valid mediaCall object
    if (!mediaCall) {
      console.error('❌ CRITICAL: handleIncomingMediaCall called with null/undefined mediaCall')
      return
    }

    if (this.callState.isInCall) {
      console.log('Already in call, rejecting incoming call')
      mediaCall.close()
      return
    }

    // CRITICAL FIX: Validate and extract core call data with extensive logging
    const callType = mediaCall.metadata?.type === 'video' ? 'video' : 'voice'
    const mediaCallPeer = mediaCall.peer
    const mediaCallMetadata = mediaCall.metadata

    console.log(`🚨 INCOMING CALL COMPREHENSIVE DEBUG:`)
    console.log(`  📞 Raw mediaCall object keys:`, Object.keys(mediaCall || {}))
    console.log(`  📞 mediaCall.peer:`, mediaCallPeer, `(type: ${typeof mediaCallPeer})`)
    console.log(`  📞 mediaCall.metadata:`, mediaCallMetadata)
    console.log(`  📞 mediaCall.metadata?.fromAlias:`, mediaCallMetadata?.fromAlias, `(type: ${typeof mediaCallMetadata?.fromAlias})`)
    console.log(`  📞 mediaCall.metadata?.fromPeerId:`, mediaCallMetadata?.fromPeerId, `(type: ${typeof mediaCallMetadata?.fromPeerId})`)
    console.log(`  📞 mediaCall.metadata?.type:`, mediaCallMetadata?.type)
    console.log(`  📞 Resolved callType:`, callType)

    // CRITICAL FIX: Multiple peer ID extraction strategies
    let extractedPeerId = null
    if (mediaCallPeer && typeof mediaCallPeer === 'string' && mediaCallPeer.length > 0) {
      extractedPeerId = mediaCallPeer
      console.log(`✅ Peer ID from mediaCall.peer: ${extractedPeerId}`)
    } else if (mediaCallMetadata?.fromPeerId && typeof mediaCallMetadata.fromPeerId === 'string') {
      extractedPeerId = mediaCallMetadata.fromPeerId
      console.log(`✅ Peer ID from metadata.fromPeerId: ${extractedPeerId}`)
    } else if (mediaCallMetadata?.from && typeof mediaCallMetadata.from === 'string') {
      extractedPeerId = mediaCallMetadata.from
      console.log(`✅ Peer ID from metadata.from: ${extractedPeerId}`)
    } else {
      console.error(`❌ CRITICAL: No valid peer ID found in mediaCall!`)
    }

    // CRITICAL FIX: Multiple alias extraction strategies
    let extractedAlias = null
    if (mediaCallMetadata?.fromAlias && typeof mediaCallMetadata.fromAlias === 'string' && mediaCallMetadata.fromAlias.trim().length > 0) {
      extractedAlias = mediaCallMetadata.fromAlias.trim()
      console.log(`✅ Alias from metadata.fromAlias: ${extractedAlias}`)
    } else if (mediaCallMetadata?.alias && typeof mediaCallMetadata.alias === 'string' && mediaCallMetadata.alias.trim().length > 0) {
      extractedAlias = mediaCallMetadata.alias.trim()
      console.log(`✅ Alias from metadata.alias: ${extractedAlias}`)
    } else if (extractedPeerId && extractedPeerId.length <= 50) {
      // Use peer ID as alias - be more permissive with format
      extractedAlias = extractedPeerId
      console.log(`✅ Using peer ID as alias: ${extractedAlias}`)
    } else {
      console.warn(`⚠️ No valid alias found in metadata - peer ID: ${extractedPeerId}`)
    }

    // CRITICAL FIX: Enhanced fallback alias generation
    let finalResolvedAlias = 'Anonymous_Caller'

    if (extractedAlias &&
        extractedAlias !== 'Unknown' &&
        extractedAlias !== 'unknown' &&
        extractedAlias !== 'Unknown User' &&
        !extractedAlias.startsWith('User_') &&
        !extractedAlias.startsWith('peer_')) {
      finalResolvedAlias = extractedAlias
      console.log(`✅ Using extracted alias: ${finalResolvedAlias}`)
    } else if (extractedPeerId) {
      // Try alias registry lookup
      if (this._aliasRegistry && typeof this._aliasRegistry.getAlias === 'function') {
        const registryAlias = this._aliasRegistry.getAlias(extractedPeerId)
        if (registryAlias &&
            typeof registryAlias === 'string' &&
            registryAlias.trim().length > 0 &&
            registryAlias !== 'unknown' &&
            registryAlias !== 'Unknown User' &&
            registryAlias !== 'Unknown') {
          finalResolvedAlias = registryAlias.trim()
          console.log(`✅ Using registry alias: ${finalResolvedAlias}`)
        } else {
          // Create meaningful fallback from peer ID - be more permissive
          if (extractedPeerId.length <= 25) {
            finalResolvedAlias = extractedPeerId
          } else {
            finalResolvedAlias = `Caller_${extractedPeerId.substring(0, 8)}`
          }
          console.log(`✅ Using peer-based fallback: ${finalResolvedAlias}`)
        }
      } else {
        // No registry, use peer-based fallback - be more permissive
        if (extractedPeerId.length <= 25) {
          finalResolvedAlias = extractedPeerId
        } else {
          finalResolvedAlias = `Caller_${extractedPeerId.substring(0, 8)}`
        }
        console.log(`✅ Using direct peer fallback: ${finalResolvedAlias}`)
      }
    } else {
      console.error(`❌ CRITICAL: No peer ID available for fallback - using Anonymous_Caller`)
    }

    console.log(`📞 FINAL RESOLUTION: ${callType} call from '${finalResolvedAlias}' (peer: ${extractedPeerId || 'undefined'})`)

    // Play incoming call sound based on call type
    notificationSoundService.playIncomingCallSound(callType).catch(error => {
      console.warn(`🔊 Failed to play incoming ${callType} call sound:`, error)
    })

    // Store the incoming call for potential acceptance
    this._currentMediaCall = mediaCall

    // CRITICAL FIX: Construct call event data with validated values
    const callEventData = {
      callType,
      fromPeerId: extractedPeerId || 'unknown_peer',
      fromAlias: finalResolvedAlias,
      mediaCall
    }

    console.log(`🚨 FINAL EVENT DATA being emitted:`, {
      callEventData,
      dataKeys: Object.keys(callEventData),
      fromPeerIdValid: !!callEventData.fromPeerId && callEventData.fromPeerId !== 'unknown_peer',
      fromAliasValid: !!callEventData.fromAlias && callEventData.fromAlias !== 'Anonymous_Caller',
      callTypeValid: !!callEventData.callType,
      hasMediaCall: !!callEventData.mediaCall
    })

    this.emit('incomingCall', callEventData)

    // Show toast notification with the resolved alias
    toastManager.info(`Incoming ${callType} call from ${finalResolvedAlias}`, TOAST_GROUPS.CALL, {
      duration: 15000,
      icon: callType === 'video' ? '📹' : '📞'
    })
  }
  
  /**
   * Clean up resources
   * CRITICAL FIX: Only cleanup call resources if we were actually in a call
   */
  cleanup(): void {
    // CRITICAL FIX: Only cleanup call if we were actually in a call
    // This prevents "Call ended" notifications during network shutdown
    if (this.callState.isInCall) {
      console.log('📞 Cleaning up active call during service cleanup')
      this.endCall(undefined, false) // No notification during cleanup
    } else {
      // Just clean up any leftover resources without call logic
      this.resetCallState()
      if (this._currentMediaCall) {
        this._currentMediaCall.close()
        this._currentMediaCall = null
      }
    }

    this.stopScreenShare()
    this.cleanupCachedStreams()

    // CRITICAL FIX: Cleanup remote audio element
    if (this.remoteAudioElement) {
      this.remoteAudioElement.pause()
      this.remoteAudioElement.srcObject = null

      // Remove from DOM if it was added
      if (document.body.contains(this.remoteAudioElement)) {
        document.body.removeChild(this.remoteAudioElement)
        console.log('🔊 Remote audio element removed from DOM')
      }

      this.remoteAudioElement = null
      console.log('🔊 Remote audio element cleaned up')
    }

    // MOBILE FIX: Clean up mobile audio interaction listeners
    this.cleanupMobileAudioListeners()

    // CONTEXTUAL CALL ENHANCEMENT: Clean up navigation tracking
    if (this.routeChangeUnsubscribe) {
      this.routeChangeUnsubscribe()
      this.routeChangeUnsubscribe = null
      console.log('🧭 Navigation tracking cleaned up')
    }

    this.pendingPermissionRequests.clear()
    this.callListeners.length = 0
    console.log('MediaService cleanup completed')
  }
  
  /**
   * Destroy service and clean up all resources
   */
  destroy(): void {
    this.cleanup()
    super.destroy() // Clean up event listeners
    console.log('MediaService destroyed')
  }

  /**
   * Reset service (for testing)
   */
  reset(): void {
    this.cleanup()
    super.destroy() // Clear event listeners
    console.log('MediaService reset completed')
  }
}